// Region control + trained-manifold mouth drive for the LIVE A2F path
// (OGSModel.driveSplatFace). Pure logic, no I/O — unit-tested in
// tests/unit/liveRigRegions.test.mjs.
//
// WHY (measured, 785 frames of a real isaac response, 2026-08-25): the A2F
// stream itself is noisy in the channels that are not speech — EyeSquintLeft
// flips direction 415 times in 785 frames (std .026), BrowInnerUp sits at a
// 0.45 mean wobbling with std .094, CheekSquint L/R flip ~370 times — while
// EyeBlink is effectively dead (max .04; the procedural blink overlay is what
// actually blinks). The live path used to write all 52 channels into the rig
// every frame with no filter, so eyes and brows visibly jittered during
// speech, which trained-pose playback (coherent rig_presets rows) never does.
//
// Two rules, both applied at the RIG level and never per-vertex — rig2mesh is
// a global 512-PCA basis and the geom/appr latent is driven by the SAME rig
// vector, so a per-region vertex blend would desync geometry from appearance
// (the "mesh closes but the decoded appearance stays put" failure the
// coupling comment in lib/rigBoard.js documents):
//
//  1. REGION MASK (maskLiveArkitRegions): zero the eye/brow/cheek/nose ARKit
//     channels of the live frame. arkitToIsaacRig adds ARKit deltas to the
//     bundle's rest pose, so a zeroed delta means "hold rest" — exactly the
//     region control asked for: mouth parameters cannot move eyes or brows.
//     The blink overlay writes AFTER the mask, so blinking is unaffected (and
//     is now cleaner: EyeWide noise no longer subtracts from the blink
//     controls through arkitToMh's a[blink] - a[wide] pairs).
//
//  2. TRAINED-MANIFOLD MOUTH MIX (buildLiveMouthDrive + applyLiveMouthMix):
//     rest + ARKit-delta lands off the training manifold (measured in
//     arkitToIsaacRig.js: further from the trained distribution than any real
//     pose), which is where the lip/mouth artifacts come from. The Rig
//     Preview panel's "Mouth open (trained)" solved this for slider edits by
//     REPLACING the mouth block with an interpolation between the two trained
//     references nearest the base (54% -> 89% rendered tooth visibility);
//     this brings the same references to the live path. The A2F frame's jaw
//     aperture picks the interpolation coordinate t, and the mouth block is
//     mixed toward that on-manifold pose by `manifoldWeight` — 1 replaces the
//     block outright, 0 keeps today's behaviour, in between keeps some raw
//     A2F articulation (funnel/pucker/press) on top of the trained base.

import { ARKIT_NAMES } from '../../lib/arkitNames.js'
import { macroSubset, parseMouthController, pickMouthReference, poseRow } from '../../lib/rigBoard.js'

// ARKit channels the live path may NOT write: everything that is not speech.
// Derived from the canonical name list so this can never drift from the 52-slot
// contract — jaw/mouth/tongue pass, eye/brow/cheek/nose are masked.
export const LIVE_MASKED_CHANNELS = ARKIT_NAMES.reduce((out, name, i) => {
  if (/^(Eye|Brow|Cheek|Nose)/.test(name)) out.push(i)
  return out
}, [])

/** Zero the non-speech channels of a live ARKit frame, in place. The blink
 *  overlay runs after this, so procedural blinks still reach the rig. */
export function maskLiveArkitRegions(arkit) {
  for (const i of LIVE_MASKED_CHANNELS) arkit[i] = 0
  return arkit
}

/** ARKit-52 channels grouped by the face region they drive, by NAME prefix. The same
 *  grouping LIVE_MASKED_CHANNELS uses, split so a bundle can decline one region rather
 *  than the whole non-speech set. */
export const ARKIT_REGION_CHANNELS = ARKIT_NAMES.reduce((out, name, i) => {
  const region = /^Eye/.test(name) ? 'eye'
    : /^Brow/.test(name) ? 'brow'
      : /^Cheek/.test(name) ? 'cheek'
        : /^Nose/.test(name) ? 'nose' : null
  if (region) (out[region] ??= []).push(i)
  return out
}, {})

/**
 * Hold whole ARKit REGIONS at rest, in place, for a bundle whose capture cannot take
 * them (`face_drives` in scene.json).
 *
 * Unlike maskLiveArkitRegions this applies to the AUTHORED frame too — face clips and
 * the idle animation — because that is where the damage was measured. A 30-row pose
 * table gives the brow block a full-rank fit, so the subspace projection cannot
 * constrain it and a single browDown at 1.0 is a combination the capture never
 * contained: over a deterministic sweep of the 52 single-channel ARKit poses on
 * myragaussainsplatting, EVERY pose that produced oversized head splats was a brow or
 * eye one (brow alone 192 of 247), and not one mouth channel did. Speech is therefore
 * untouched by this — which is the point, and why it is per region and not a blanket
 * "hold the face still".
 *
 * @param {Float32Array} arkit  the composed 52-channel frame, modified in place
 * @param {Record<string, boolean>} drives  region -> may it drive; absent = yes
 */
export function maskArkitRegions(arkit, drives) {
  if (!drives) return arkit
  for (const [region, channels] of Object.entries(ARKIT_REGION_CHANNELS)) {
    if (drives[region] === false) for (const i of channels) arkit[i] = 0
  }
  return arkit
}

/** Default manifold weight for the live mouth mix. Leans on the trained
 *  references while keeping some raw A2F articulation; eyeball-tuned, and the
 *  `?livemouth=` URL param overrides it per session for A/B. */
export const DEFAULT_LIVE_MOUTH_WEIGHT = 0.65

/**
 * Resolve the trained mouth references the live path will interpolate.
 * Preference order mirrors the Rig Preview panel: the bundle's base-conditioned
 * `mouth_controller` (references picked nearest the REST pose — the live path's
 * base — via the same pickMouthReference the panel uses), else the global
 * `macros` pair. Returns null when the bundle supports neither — the caller
 * then simply keeps the rest+delta path, never a fake drive.
 *
 * Built once per character load (rest and the pose table arrive together in
 * OGSModel.resolveRestRig); nothing here runs per frame.
 *
 * @param {object} args
 * @param {object|null} args.scene   parsed scene.json (raw snake_case)
 * @param {string[]|null} args.names rig_names.json
 * @param {Float32Array|null} args.presets flat pose table (rows * dim)
 * @param {number} args.dim          control-space width
 * @param {ArrayLike<number>|null} args.rest the bundle's rest pose (length dim)
 * @returns {{subset:number[], closedRef:Float32Array, openRef:Float32Array,
 *            jaw:number, jawLo:number, jawHi:number, source:string,
 *            closedRow:number, openRow:number}|null}
 */
export function buildLiveMouthDrive({ scene, names, presets, dim, rest }) {
  if (!presets || !dim || presets.length < dim) return null

  const spec = parseMouthController(scene?.mouth_controller)
  let subset = null
  let closedRow = -1
  let openRow = -1
  let jaw = -1
  let source = null

  if (spec && rest) {
    closedRow = pickMouthReference(spec, rest, spec.closedPool, presets, dim, 'closed')
    openRow = pickMouthReference(spec, rest, spec.openPool, presets, dim, 'open')
    subset = spec.mouthIdx.filter((i) => i >= 0 && i < dim)
    jaw = spec.jaw
    source = 'mouth_controller'
  }

  // Global-macro fallback: the declared open/closed pose pair, over the
  // fragment-derived mouth subset — the same references the panel's
  // "Mouth open (macro)" row uses.
  if ((closedRow < 0 || openRow < 0 || !subset?.length) && Array.isArray(names)) {
    const o = scene?.macros?.mouth_open_pose
    const c = scene?.macros?.mouth_closed_pose
    if (Number.isInteger(o) && Number.isInteger(c)) {
      subset = macroSubset(names, 'mouth')
      closedRow = c
      openRow = o
      jaw = names.findIndex((n) => n.includes('CTRL_C_jaw.translateY'))
      source = 'macros'
    }
  }

  if (!subset?.length || closedRow < 0 || openRow < 0) return null
  if (!Number.isInteger(jaw) || jaw < 0 || jaw >= dim) return null

  const closedRef = poseRow(presets, dim, closedRow)
  const openRef = poseRow(presets, dim, openRow)
  if (closedRef.length !== dim || openRef.length !== dim) return null

  const jawLo = closedRef[jaw]
  const jawHi = openRef[jaw]
  // A degenerate jaw span cannot carry the aperture coordinate — no drive,
  // rather than one whose t is numeric noise.
  if (!(Math.abs(jawHi - jawLo) > 1e-6)) return null

  return { subset, closedRef, openRef, jaw, jawLo, jawHi, source, closedRow, openRow }
}

/**
 * Mix the rig's mouth block toward the trained closed->open interpolation, in
 * place. Hot path: no allocation, one pass over the subset.
 *
 * t is read off the jaw control of the INCOMING rig (rest + A2F delta), so the
 * A2F stream still owns the aperture; the trained references own what a mouth
 * at that aperture looks like.
 *
 * @param {Float32Array} rig  full-space rig (mutated)
 * @param {object} drive      buildLiveMouthDrive result
 * @param {number} manifoldWeight 0 = leave rig, 1 = pure trained interpolation
 * @returns {number} the aperture coordinate t (0..1), for debug readouts
 */
export function applyLiveMouthMix(rig, drive, manifoldWeight) {
  const { subset, closedRef, openRef, jaw, jawLo, jawHi } = drive
  let t = (rig[jaw] - jawLo) / (jawHi - jawLo)
  if (!(t > 0)) t = 0
  else if (t > 1) t = 1
  let w = manifoldWeight
  if (!(w > 0)) return t
  if (w > 1) w = 1
  for (const i of subset) {
    const m = (1 - t) * closedRef[i] + t * openRef[i]
    rig[i] += w * (m - rig[i])
  }
  return t
}
